// The seven days behind one timesheet row (LT-01 / LT-02 / LT-03).
//
// A sheet row is a single number per bucket. The person approving it, and the
// person signing it, need to see the days it is made of: what was worked, which
// days were approved leave, which were a public holiday or a rest day, and where
// someone punched on a day of leave. Both the sheet-addressed and the
// week-addressed route answer the same payload, so one panel serves the manager's
// drawer and the employee's own week (a week with no sheet still shows its leave).
//
// It is a vertical list, not a seven-column strip: the dialog is 560px wide and
// the employee page shares the component, so the layout must not depend on width.
//
// Hours are DECIMALS, never money — nothing here goes through <Money>.

import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import {
  Badge,
  Banner,
  Button,
  ErrorState,
  Loading,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@novemai/components";

import { errorMessage } from "@/api/errors";
import { formatDateRange } from "@/lib/date-range";
import { formatHours } from "@/lib/hours";

import { fetchTimesheetDays, type TimesheetDay, type TimesheetDaysView } from "./time-api";

/** Date-only strings are calendar days, so they are drawn under a UTC pin like formatDateRange. */
function formatDayHeading(locale: string, isoDate: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${isoDate}T00:00:00Z`));
}

function DayRow({ day }: { readonly day: TimesheetDay }): React.ReactElement {
  const { t, i18n } = useTranslation();
  const hours = (value: string): string => formatHours(value, i18n.language) ?? value;
  const heading = formatDayHeading(i18n.language, day.date);
  const worked = Number(day.worked_hours) > 0;
  // A rest day that payroll still deducts (a weekend inside unpaid leave) has no
  // leave line of its own; without this it would look like an ordinary day off.
  const payrollOnly = day.payroll_unpaid && day.leave.length === 0;

  return (
    <li
      aria-label={heading}
      data-day={day.date}
      className={`grid grid-cols-[6.5rem_1fr_auto] items-start gap-3 rounded-lg border border-[var(--border-subtle)] px-3 py-2.5 ${
        day.day_type === "workday" ? "" : "bg-[var(--bg-subtle)]"
      }`}
    >
      <span className="font-serif text-[14px] text-[var(--fg-primary)]">{heading}</span>

      <div className="flex flex-wrap items-center gap-1.5">
        {day.day_type === "rest_day" ? (
          <Badge tone="neutral">{t("time.days.rest_day")}</Badge>
        ) : null}
        {day.day_type === "public_holiday" || day.day_type === "public_holiday_half" ? (
          <Badge tone="info">
            {day.holiday_name ??
              t(
                day.day_type === "public_holiday"
                  ? "time.days.public_holiday"
                  : "time.days.public_holiday_half",
              )}
          </Badge>
        ) : null}
        {day.leave.map((item) => (
          <Badge key={item.request_id} tone={item.unpaid ? "warning" : "success"}>
            {t(item.unpaid ? "time.days.leave_unpaid" : "time.days.leave_paid", {
              hours: hours(item.hours),
            })}
          </Badge>
        ))}
        {payrollOnly ? <Badge tone="warning">{t("time.days.payroll_unpaid")}</Badge> : null}
        {day.leave_day_worked ? (
          <Badge tone="danger">{t("time.days.leave_day_worked")}</Badge>
        ) : null}
        {day.needs_review ? <Badge tone="warning">{t("time.entry.review_badge")}</Badge> : null}
      </div>

      <span
        className={`font-mono text-[13px] tabular-nums ${
          worked ? "text-[var(--fg-primary)]" : "text-[var(--fg-muted)]"
        }`}
      >
        {worked
          ? t("time.days.hours_value", { hours: hours(day.worked_hours) })
          : t("time.cell.dash")}
      </span>
    </li>
  );
}

export function WeekDaysPanel({ view }: { readonly view: TimesheetDaysView }): React.ReactElement {
  const { t, i18n } = useTranslation();
  const hours = (value: string): string => formatHours(value, i18n.language) ?? value;
  const totals: readonly { readonly key: string; readonly value: string }[] = [
    { key: "regular", value: view.totals.regular },
    { key: "ot", value: view.totals.ot },
    { key: "leave", value: view.totals.pto },
    { key: "unpaid", value: view.totals.unpaid },
    { key: "holiday", value: view.totals.holiday },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-sans text-[13px] text-[var(--fg-secondary)]">
          {formatDateRange(i18n.language, view.week_start, view.week_end)}
        </span>
        <Badge tone={view.provisional ? "warning" : "info"}>
          {t(view.provisional ? "time.days.provisional" : "time.days.final")}
        </Badge>
        {view.timesheet === null ? (
          <Badge tone="neutral">{t("time.days.no_timesheet")}</Badge>
        ) : null}
      </div>

      {view.leave_snapshot_stale ? (
        <Banner severity="warning" title={t("time.days.stale_title")}>
          {t("time.days.stale_body")}
        </Banner>
      ) : null}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-5">
        {totals.map(({ key, value }) => (
          <div key={key}>
            <dt className="font-sans text-[12px] text-[var(--fg-muted)]">
              {t(`time.days.total.${key}`)}
            </dt>
            <dd className="font-mono text-[14px] tabular-nums text-[var(--fg-primary)]">
              {hours(value)}
            </dd>
          </div>
        ))}
      </dl>

      {view.provisional ? (
        <p className="font-sans text-[12.5px] leading-relaxed text-[var(--fg-muted)]">
          {t("time.days.provisional_hint")}
        </p>
      ) : null}

      <ol className="space-y-2" aria-label={t("time.days.list_label")}>
        {view.days.map((day) => (
          <DayRow key={day.date} day={day} />
        ))}
      </ol>
    </div>
  );
}

/** The manager's drawer: fetches by sheet id, so it always agrees with the row it was opened from. */
export function TimesheetDaysDialog({
  timesheetId,
  employeeName,
  onClose,
}: {
  readonly timesheetId: string;
  readonly employeeName: string | null;
  readonly onClose: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ["time", "timesheet-days", timesheetId],
    queryFn: () => fetchTimesheetDays(timesheetId),
  });

  return (
    <Modal open onClose={onClose}>
      <ModalHeader
        title={
          employeeName === null
            ? t("time.days.title")
            : t("time.days.title_named", { name: employeeName })
        }
      />
      <ModalBody>
        {query.isLoading ? (
          <Loading label={t("time.days.loading")} rows={4} />
        ) : query.isError ? (
          <ErrorState
            title={t("time.days.error_title")}
            description={errorMessage(query.error, t)}
          />
        ) : query.data !== undefined ? (
          <WeekDaysPanel view={query.data} />
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" size="sm" onClick={onClose}>
          {t("time.days.close")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
